import type { AxiosInstance } from "axios";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  Info,
  Layers,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DateRangePicker } from "../components/DateRangePicker.js";
import {
  FormulaList,
  HighlightCard,
  HighlightGrid,
  noteText,
} from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Button } from "../components/ui/button.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import {
  daysBefore,
  rangeProblem,
  toLocalDate,
  type DateRange,
} from "../lib/dates.js";
import { PAGE_SIZES } from "../lib/reports.js";
import { sourceMark } from "../lib/source-marks.js";

/**
 * Attribution Model Analysis.
 * Ported from legacy `ecom_code` (`AttributionModelAnalysis.jsx`).
 *
 * Provides a side-by-side comparison of 5 core attribution models:
 *   1. First-Click (FC)
 *   2. Last-Click (LC)
 *   3. Linear
 *   4. Time-Decay (TD)
 *   5. Data-Driven Attribution (DDA)
 *
 * Across 4 hierarchy drill-down tiers:
 *   Source -> Campaign -> AdSet / AdGroup -> Ad Creative
 *
 * Uses strict platform design tokens: Inter/Outfit typography, clean border-separated
 * tables, sticky headers, subtle row shading, breadcrumb trail, and accessible guide notes.
 */

export interface AttributionModelItem {
  readonly id: string;
  readonly name: string;
  readonly channel: string;
  readonly fc_revenue: number;
  readonly first_click_pct: number;
  readonly lc_revenue: number;
  readonly last_click_pct: number;
  readonly linear_revenue: number;
  readonly linear_pct: number;
  readonly td_revenue: number;
  readonly time_decay_pct: number;
  readonly dda_revenue: number;
  readonly dda_pct: number;
  readonly dda_vs_last_click_pp: number;
}

export interface AttributionModelTotals {
  readonly totalFcRev: number;
  readonly totalLcRev: number;
  readonly totalLinearRev: number;
  readonly totalTdRev: number;
  readonly totalDdaRev: number;
  readonly first_click_pct: number;
  readonly last_click_pct: number;
  readonly linear_pct: number;
  readonly time_decay_pct: number;
  readonly dda_pct: number;
}

export interface AttributionModelAnalysisResponse {
  readonly from: string;
  readonly to: string;
  readonly level: "source" | "campaign" | "adset" | "ad";
  readonly channel: string;
  readonly campaign_id: string;
  readonly adset_id: string;
  readonly channels: readonly AttributionModelItem[];
  readonly totals: AttributionModelTotals;
  /** This drill-down level's TRUE row count — before `limit`/`offset`, for the pager. */
  readonly totalEntityCount: number;
  readonly limit: number;
  readonly offset: number;
}

export type HierarchyLevel = "source" | "campaign" | "adset" | "ad";

const DRILLABLE_SOURCES = ["meta", "google"];

function defaultAttributionRange(today: Date = new Date()): DateRange {
  return { from: toLocalDate(daysBefore(today, 29)), to: toLocalDate(today) };
}

function pctFormat(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(val)) return "—";
  return `${val.toFixed(2)}%`;
}

/** Half the tooltip's own width (`w-60` = 240px) — used to clamp it inside the viewport. */
const COLUMN_TOOLTIP_HALF_WIDTH = 120;

/**
 * `createPortal`/`position: fixed`, same technique as `CampaignStageAnalysisScreen.tsx`'s
 * bar tooltip and `LatentValueAnalysisScreen.tsx`'s campaign dropdown — plain absolute
 * positioning here was clipped by the table's own `overflow-auto` scroll container the
 * instant this button sat inside a sticky header, which every column header here does.
 */
function ColumnTooltip({ text }: { readonly text: string }): React.JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const showTooltip = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setCoords({
      top: rect.bottom + 8,
      left: Math.min(Math.max(rect.left + rect.width / 2, COLUMN_TOOLTIP_HALF_WIDTH + 8), window.innerWidth - COLUMN_TOOLTIP_HALF_WIDTH - 8),
    });
  };
  const hideTooltip = (): void => setCoords(null);

  return (
    <span className="inline-flex items-center">
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        aria-label="More info"
        className="cursor-pointer text-muted-foreground hover:text-foreground inline-flex items-center p-0.5"
      >
        <Info className="size-3.5" />
      </button>
      {coords !== null &&
        createPortal(
          <span
            role="tooltip"
            style={{ position: "fixed", top: coords.top, left: coords.left, transform: "translateX(-50%)" }}
            className="pointer-events-none z-50 w-60 rounded-md bg-[#262626] px-2.5 py-1.5 text-center text-xs font-normal text-white shadow-lg"
          >
            <span
              aria-hidden="true"
              className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-[#262626]"
            />
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

export function AttributionModelAnalysisScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
}): React.JSX.Element {
  const [range, setRange] = useState<DateRange>(() => defaultAttributionRange());
  const [level, setLevel] = useState<HierarchyLevel>("source");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState({ id: "", name: "" });
  const [selectedAdset, setSelectedAdset] = useState({ id: "", name: "" });

  const [data, setData] = useState<AttributionModelAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGuideSection, setShowGuideSection] = useState(false);
  const { colWidths, startResize } = useTableResize([280, 160, 160, 160, 160, 160]);

  // Only a drill-down level (Campaign/AdSet/Ad) tends to need this — a Meta or Google
  // account can easily have hundreds of campaigns — but it applies at every level for
  // the same reason ReportScreen.tsx's own pager does: consistent behaviour beats a
  // special case for "usually small."
  const [limit, setLimit] = useState<number>(50);
  const [offset, setOffset] = useState(0);

  const [sortKey, setSortKey] = useState<keyof AttributionModelItem | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc" | null>(null);

  const problem = useMemo(() => rangeProblem(range), [range]);

  const load = useCallback(async () => {
    if (problem !== null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{ success: boolean; data: AttributionModelAnalysisResponse }>(
        "/api/v2/attribution-model-analysis",
        {
          params: {
            from: range.from,
            to: range.to,
            level,
            channel: selectedChannel,
            campaign_id: selectedCampaign.id,
            adset_id: selectedAdset.id,
            limit,
            offset,
          },
        },
      );
      setData(res.data.data);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load attribution model analysis. Please verify your connection."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client, range.from, range.to, level, selectedChannel, selectedCampaign.id, selectedAdset.id, limit, offset, problem]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  const handleSortToggle = (colKey: keyof AttributionModelItem): void => {
    if (sortKey !== colKey) {
      setSortKey(colKey);
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.channels) return [];
    if (!sortKey || !sortDir) return data.channels;
    const copy = [...data.channels];
    copy.sort((a, b) => {
      if (sortKey === "channel" || sortKey === "name" || sortKey === "id") {
        const strA = String(a.name || a.channel || a.id || "").toLowerCase();
        const strB = String(b.name || b.channel || b.id || "").toLowerCase();
        if (strA < strB) return sortDir === "asc" ? -1 : 1;
        if (strA > strB) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
      const numA = Number(a[sortKey] ?? 0);
      const numB = Number(b[sortKey] ?? 0);
      return sortDir === "asc" ? numA - numB : numB - numA;
    });
    return copy;
  }, [data?.channels, sortKey, sortDir]);

  const canDrillRow = (row: AttributionModelItem): boolean => {
    const rowId = row.id || row.channel || row.name;
    const rowName = row.name || row.channel || row.id || "";

    if (level === "source") {
      const src = (rowId || "").toLowerCase().trim();
      return DRILLABLE_SOURCES.includes(src);
    }
    if (level === "campaign" || level === "adset") {
      return rowId !== "unidentified" && !rowName.toLowerCase().includes("unidentified") && rowId !== "";
    }
    return false;
  };

  const handleDrilldown = (row: AttributionModelItem): void => {
    if (!canDrillRow(row)) return;

    // A new scope is a new row set — page 2 of the OLD one is not a page of the new
    // one, and could easily be past its end.
    setOffset(0);
    if (level === "source") {
      const src = (row.channel || row.id || "").toLowerCase().trim();
      setSelectedChannel(src);
      setLevel("campaign");
    } else if (level === "campaign") {
      setSelectedCampaign({ id: row.id, name: row.name || row.id });
      setLevel("adset");
    } else if (level === "adset") {
      setSelectedAdset({ id: row.id, name: row.name || row.id });
      setLevel("ad");
    }
  };

  const navigateToLevel = (target: HierarchyLevel): void => {
    setOffset(0);
    if (target === "source") {
      setSelectedChannel("");
      setSelectedCampaign({ id: "", name: "" });
      setSelectedAdset({ id: "", name: "" });
      setLevel("source");
    } else if (target === "campaign") {
      setSelectedCampaign({ id: "", name: "" });
      setSelectedAdset({ id: "", name: "" });
      setLevel("campaign");
    } else if (target === "adset") {
      setSelectedAdset({ id: "", name: "" });
      setLevel("adset");
    }
  };

  const handleBack = (): void => {
    if (level === "ad") navigateToLevel("adset");
    else if (level === "adset") navigateToLevel("campaign");
    else navigateToLevel("source");
  };

  const firstColumnLabel = useMemo(() => {
    if (level === "source") return "Channel";
    if (level === "campaign") return "Campaign Name";
    if (level === "adset") return "AdSet / AdGroup Name";
    return "Ad Name";
  }, [level]);

  const subtitle = useMemo(() => {
    if (level === "source") return "Side-by-side comparison of 5 core attribution models across marketing channels.";
    if (level === "campaign") return `Comparing attribution models across ${selectedChannel.toUpperCase()} campaigns.`;
    if (level === "adset") return `Comparing attribution models across ad sets for ${selectedCampaign.name || selectedCampaign.id}.`;
    return `Comparing attribution models across ads for ${selectedAdset.name || selectedAdset.id}.`;
  }, [level, selectedChannel, selectedCampaign, selectedAdset]);

  const isEmpty = !loading && sortedRows.length === 0;

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <Layers className="text-primary size-6" />
            Attribution Model Analysis
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
              showLabel="Click to view Attribution Model Guide & Logic"
              hideLabel="Hide Guide & Logic"
            />
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker
            value={range}
            onApply={(next) => {
              setOffset(0);
              setRange(next);
            }}
          />
        </div>
      </header>

      {/* Report Guide Accordion */}
      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatTitle="What Attribution Model Analysis shows"
        whatBadge="Model Guide"
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              Attribution Model Analysis compares 5 attribution models side-by-side so you can identify which channels,
              campaigns, and creatives drive initial discovery versus final order conversion:
            </p>
            <HighlightGrid cols={3}>
              <HighlightCard tone="blue" title="1. 5 Core Attribution Models" icon={<Layers className="size-3.5" />}>
                First-Click, Last-Click, Linear (1/N), Time-Decay (recency weighted), and Data-Driven — this store's own
                DDA engine, the same one behind Source and Campaign Attribution.
              </HighlightCard>
              <HighlightCard tone="purple" title="2. 4 Drilldown Funnel Levels" icon={<TrendingUp className="size-3.5" />}>
                Drill from high-level Channel down into Campaign, Ad Set / Ad Group, and individual Ad Creative performance.
              </HighlightCard>
              <HighlightCard tone="emerald" title="3. Top-of-Funnel vs Bottom-of-Funnel" icon={<Sparkles className="size-3.5" />}>
                A channel with much higher First-Click than Last-Click is introducing customers; one with higher
                Last-Click is closing sales other channels started.
              </HighlightCard>
            </HighlightGrid>
          </div>
        }
        howTitle="How attribution models & credit percentages work"
        howBadge="Attribution Logic"
        howContent={
          <div className={noteText}>
            <p className="m-0">How model percentages and channel insights are calculated:</p>
            <FormulaList>
              <li>
                <strong>📈 Top-of-Funnel vs Bottom-of-Funnel Identification:</strong> If a channel has a significantly
                higher <strong>First-Click %</strong> than <strong>Last-Click %</strong> (e.g. Meta / Instagram Top-of-Funnel
                ads), it acts as an <em>introducer / brand discovery engine</em>. If <strong>Last-Click %</strong> is higher
                (e.g. Google Brand Search or Direct), it acts as a <em>closer / final converter</em>.
              </li>
              <li>
                <strong>⏱️ Time-Decay Weighting Formula:</strong> Assigns weight to each session using{" "}
                <code>1 / (days_till_purchase + 1)</code>. A session that occurred on the day of checkout receives
                significantly higher credit than an ad click that occurred 10 days earlier.
              </li>
              <li>
                <strong>🧠 Data-Driven Attribution (DDA):</strong> This store's own DDA engine — the same credit shown
                on Source and Campaign Attribution — scores every touch by what it did (page views, add-to-cart,
                closing the sale) and how recently, then splits the order&apos;s value across touches in proportion to
                those scores. A sale that closes on a Direct visit hands some of that credit back to the channels that
                actually did the persuading.
              </li>
            </FormulaList>
          </div>
        }
      />

      {problem !== null && (
        <p className="text-destructive text-sm" role="alert">
          {problem}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      {/* Breadcrumb Navigation Trail when drilled down */}
      {level !== "source" && (
        <div className="flex items-center justify-between rounded-xl border bg-muted/30 px-4 py-2.5 text-xs font-medium">
          <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <button
              type="button"
              onClick={() => navigateToLevel("source")}
              className="hover:text-foreground hover:underline transition-colors"
            >
              All Sources
            </button>
            <ChevronRight className="size-3.5 text-muted-foreground/60" />

            {level === "campaign" ? (
              <span className="font-semibold text-foreground uppercase">{selectedChannel} (Campaigns)</span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigateToLevel("campaign")}
                  className="hover:text-foreground hover:underline uppercase transition-colors"
                >
                  {selectedChannel}
                </button>
                <ChevronRight className="size-3.5 text-muted-foreground/60" />
              </>
            )}

            {level === "adset" && (
              <span className="font-semibold text-foreground truncate max-w-xs" title={selectedCampaign.name}>
                {selectedCampaign.name} (AdSets)
              </span>
            )}

            {level === "ad" && (
              <>
                <button
                  type="button"
                  onClick={() => navigateToLevel("adset")}
                  className="hover:text-foreground hover:underline truncate max-w-xs transition-colors"
                  title={selectedCampaign.name}
                >
                  {selectedCampaign.name}
                </button>
                <ChevronRight className="size-3.5 text-muted-foreground/60" />
                <span className="font-semibold text-foreground truncate max-w-xs" title={selectedAdset.name}>
                  {selectedAdset.name} (Ads)
                </span>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium shadow-xs hover:bg-muted transition-all cursor-pointer"
          >
            <ArrowLeft className="size-3.5" />
            <span>Back</span>
          </button>
        </div>
      )}

      {/* Main Table */}
      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="max-h-[min(70svh,48rem)] overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  onClick={() => handleSortToggle(level === "source" ? "channel" : "name")}
                  className="row-head text-foreground sticky top-0 left-0 z-30 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold cursor-pointer select-none relative"
                >
                  <div className="flex items-center justify-between gap-2 pr-2">
                    <div className="flex items-center gap-1.5">
                      <span>{firstColumnLabel}</span>
                      <ColumnTooltip
                        text={
                          level === "source"
                            ? "Channel / EA Source (Click Meta or Google to drill into Campaigns)"
                            : level === "campaign"
                            ? "Campaign Name (Click to view AdSets)"
                            : level === "adset"
                            ? "AdSet / AdGroup Name (Click to view Ads)"
                            : "Ad Creative Name"
                        }
                      />
                    </div>
                    <SortIndicator
                      active={sortKey === "channel" || sortKey === "name"}
                      direction={sortDir}
                    />
                  </div>
                  <TableResizeHandle onResize={(e) => startResize(0, e)} />
                </th>

                <SortableHeader
                  label="First-Click"
                  columnKey="first_click_pct"
                  tooltip="Allocates 100% of order credit to the first customer touchpoint."
                  currentSort={sortKey}
                  direction={sortDir}
                  onToggle={handleSortToggle}
                  onResize={(e) => startResize(1, e)}
                />

                <SortableHeader
                  label="Last-Click"
                  columnKey="last_click_pct"
                  tooltip="Allocates 100% of order credit to the final touchpoint before checkout."
                  currentSort={sortKey}
                  direction={sortDir}
                  onToggle={handleSortToggle}
                  onResize={(e) => startResize(2, e)}
                />

                <SortableHeader
                  label="Linear"
                  columnKey="linear_pct"
                  tooltip="Distributes credit equally (1/N) across all touchpoints in the journey."
                  currentSort={sortKey}
                  direction={sortDir}
                  onToggle={handleSortToggle}
                  onResize={(e) => startResize(3, e)}
                />

                <SortableHeader
                  label="Time-Decay"
                  columnKey="time_decay_pct"
                  tooltip="Decays weight based on 1 / (days_till_purchase + 1) — favouring recent touches."
                  currentSort={sortKey}
                  direction={sortDir}
                  onToggle={handleSortToggle}
                  onResize={(e) => startResize(4, e)}
                />

                <SortableHeader
                  label="DDA"
                  columnKey="dda_pct"
                  tooltip="Data-Driven Attribution — Algorithmic multi-touch model balancing journey position and engagement."
                  currentSort={sortKey}
                  direction={sortDir}
                  onToggle={handleSortToggle}
                  onResize={(e) => startResize(5, e)}
                />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }, (_, i) => (
                  <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-48" />
                    </td>
                    <td className="border-b px-3 py-3 text-right">
                      <Skeleton className="ml-auto h-4 w-14" />
                    </td>
                    <td className="border-b px-3 py-3 text-right">
                      <Skeleton className="ml-auto h-4 w-14" />
                    </td>
                    <td className="border-b px-3 py-3 text-right">
                      <Skeleton className="ml-auto h-4 w-14" />
                    </td>
                    <td className="border-b px-3 py-3 text-right">
                      <Skeleton className="ml-auto h-4 w-14" />
                    </td>
                    <td className="border-b px-3 py-3 text-right">
                      <Skeleton className="ml-auto h-4 w-14" />
                    </td>
                  </tr>
                ))
              ) : isEmpty ? (
                <tr>
                  <td colSpan={6} className="text-muted-foreground px-4 py-14 text-center text-sm">
                    No attribution data found for {storeName} in this date range.
                  </td>
                </tr>
              ) : (
                <>
                  {sortedRows.map((row, i) => {
                    const isClickable = canDrillRow(row);
                    const displayName = row.name || row.channel || row.id || "—";
                    const isUnidentified = displayName.toLowerCase().includes("unidentified");
                    const mark = level === "source" ? sourceMark(row.channel || row.id) : null;

                    return (
                      <tr
                        key={row.id || row.channel || i}
                        className={cn(
                          i % 2 === 1 ? "row-odd" : "row-even",
                          "hover:bg-muted/40 transition-colors",
                        )}
                      >
                        <td className="border-b px-3 py-2.5">
                          <div
                            onClick={() => isClickable && handleDrilldown(row)}
                            className={cn(
                              "flex items-center gap-2 max-w-md overflow-hidden",
                              isClickable && "cursor-pointer group text-primary font-medium hover:underline",
                              !isClickable && !isUnidentified && "font-medium text-foreground",
                              isUnidentified && "text-muted-foreground italic",
                            )}
                          >
                            {mark?.Icon && (
                              <mark.Icon className="size-4 shrink-0" style={{ color: mark.color }} />
                            )}
                            <span className="truncate" title={displayName}>
                              {displayName}
                            </span>
                            {isClickable && (
                              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                            )}
                          </div>
                        </td>

                        <td className="border-b px-3 py-2.5 text-right font-medium text-foreground">
                          {pctFormat(row.first_click_pct)}
                        </td>

                        <td className="border-b px-3 py-2.5 text-right font-medium text-foreground">
                          {pctFormat(row.last_click_pct)}
                        </td>

                        <td className="border-b px-3 py-2.5 text-right font-medium text-foreground">
                          {pctFormat(row.linear_pct)}
                        </td>

                        <td className="border-b px-3 py-2.5 text-right font-medium text-foreground">
                          {pctFormat(row.time_decay_pct)}
                        </td>

                        <td className="border-b px-3 py-2.5 text-right font-semibold text-foreground">
                          {pctFormat(row.dda_pct)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Summary Totals Row */}
                  {data?.totals && (
                    <tr className="bg-muted/40 font-bold border-t-2">
                      <td className="border-b px-3 py-3 text-foreground">Total</td>
                      <td className="border-b px-3 py-3 text-right text-foreground">
                        {pctFormat(data.totals.first_click_pct)}
                      </td>
                      <td className="border-b px-3 py-3 text-right text-foreground">
                        {pctFormat(data.totals.last_click_pct)}
                      </td>
                      <td className="border-b px-3 py-3 text-right text-foreground">
                        {pctFormat(data.totals.linear_pct)}
                      </td>
                      <td className="border-b px-3 py-3 text-right text-foreground">
                        {pctFormat(data.totals.time_decay_pct)}
                      </td>
                      <td className="border-b px-3 py-3 text-right text-foreground">
                        {pctFormat(data.totals.dda_pct)}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/*
        Same pager as ReportScreen.tsx: a real, server-computed total (this report DOES
        emit one — total_entity_count — so "Rows 1-50 of 214" is always true, not just
        "there might be more"), Previous/Next, and a rows-per-page Select capped at the
        API's own limit options.
      */}
      {(offset > 0 || (data !== null && offset + sortedRows.length < data.totalEntityCount)) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav aria-label="Pagination" className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <span className="text-muted-foreground text-xs">
              Rows {offset + 1}–{offset + sortedRows.length}
              {data !== null && ` of ${data.totalEntityCount.toLocaleString("en-IN")}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={data === null || offset + limit >= data.totalEntityCount || loading}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </nav>

          <div className="flex items-center gap-2">
            <Label htmlFor="attribution-rows" className="text-muted-foreground text-xs">
              Rows
            </Label>
            <Select
              id="attribution-rows"
              className="h-9 w-[5.5rem]"
              value={limit}
              onChange={(event) => {
                setOffset(0);
                setLimit(Number(event.target.value));
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
        </div>
      )}
    </section>
  );
}

function SortIndicator({
  active,
  direction,
}: {
  readonly active: boolean;
  readonly direction: "asc" | "desc" | null;
}): React.JSX.Element {
  if (!active || direction === null) {
    return <ArrowUpDown className="size-3.5 text-muted-foreground/40 shrink-0" />;
  }
  return direction === "asc" ? (
    <ArrowUp className="size-3.5 text-primary shrink-0" />
  ) : (
    <ArrowDown className="size-3.5 text-primary shrink-0" />
  );
}

function SortableHeader({
  label,
  columnKey,
  tooltip,
  currentSort,
  direction,
  onToggle,
  onResize,
}: {
  readonly label: string;
  readonly columnKey: keyof AttributionModelItem;
  readonly tooltip: string;
  readonly currentSort: keyof AttributionModelItem | null;
  readonly direction: "asc" | "desc" | null;
  readonly onToggle: (key: keyof AttributionModelItem) => void;
  readonly onResize?: (event: React.MouseEvent) => void;
}): React.JSX.Element {
  const active = currentSort === columnKey;

  return (
    <th
      onClick={() => onToggle(columnKey)}
      className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-right text-[13px] font-semibold cursor-pointer select-none relative"
    >
      <div className="flex items-center justify-end gap-1.5 pr-2">
        <span>{label}</span>
        <ColumnTooltip text={tooltip} />
        <SortIndicator active={active} direction={direction} />
      </div>
      {onResize && <TableResizeHandle onResize={onResize} />}
    </th>
  );
}
